import { Injectable } from '@angular/core';
import { User, UserDetails } from '@coach-bot/data-access/shared/models';
import * as Sentry from '@sentry/angular';
import { ApiRequestType, ExtensionCommunicationService } from '@shared/extension';
import { AuthError } from '../classes';
import { AuthActionType, AuthErrorCode } from '../enums';
import { GET_USER_DETAILS_QUERY } from '../queries';

interface AuthException {
  code?: AuthErrorCode;
  message?: string;
}

const USER_DISABLED_EXCEPTION_MESSAGE = 'User is disabled.';

@Injectable()
export class AuthService {
  constructor(private extensionCommunicationService: ExtensionCommunicationService) {}

  async forgotPassword(email: string): Promise<void> {
    try {
      await this.extensionCommunicationService.sendApiRequest({ type: ApiRequestType.ForgotPassword, data: email });
    } catch (ex: unknown) {
      console.log('Forgot Password error:', ex);
      throw new AuthError(AuthActionType.ForgotPassword, this.getExceptionCode(ex));
    }
  }

  async getUserDetails(userId: string): Promise<UserDetails> {
    return this.extensionCommunicationService
      .getAppSyncQueryData<{ getUserDetails: UserDetails }>(GET_USER_DETAILS_QUERY, { userId })
      .then(response => response.getUserDetails);
  }

  async signIn(username: string, password: string): Promise<User> {
    try {
      return await this.extensionCommunicationService.getApiRequestData({
        type: ApiRequestType.SignIn,
        data: { username, password },
      });
    } catch (ex: unknown) {
      console.log('Sign In error:', ex);
      throw new AuthError(AuthActionType.SignIn, this.getExceptionCode(ex));
    }
  }

  async signOut(): Promise<void> {
    try {
      await this.extensionCommunicationService.sendApiRequest({ type: ApiRequestType.SignOut });
    } catch (ex: unknown) {
      console.log('Sign Out error:', ex);
      throw new AuthError(AuthActionType.SignOut, this.getExceptionCode(ex));
    }
  }

  updateUser(user: User): void {
    if (user) {
      Sentry.setUser({
        id: user.attributes['custom:userID'],
        studentId: user.attributes['custom:studentID'],
        email: user.attributes.email,
        name: `${user.attributes.name} ${user.attributes.family_name}`,
      });
    } else {
      Sentry.setUser(null);
    }
  }

  private getExceptionCode(ex: unknown): AuthErrorCode {
    const authException = ex as AuthException;
    let code: AuthErrorCode = authException.code;
    if (authException.message === USER_DISABLED_EXCEPTION_MESSAGE) {
      code = AuthErrorCode.UserDisabledException;
    }
    return code;
  }
}
